import { Col, Flex, Row } from 'antd';
import { upperFirst } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import useT from '@tools/ui-hooks/useT';
import { theme } from '../../theme/semanticTokens';
import { Typography } from '../../Typography';
import { RemovableTag } from '../RemovableTag/RemovableTag';

export interface FilterHeaderProps {
  filters: Record<string, any>;
  onRemoveFilter: (key: string, value?: any) => void;
  onClearAll: () => void;
  resultCount?: number;
  splitValues?: boolean;
}

const StyledLinkWrapper = styled.div({
  cursor: 'pointer',
  '&:hover': {
    '& > *': {
      color: theme.dime.colors.link.primaryHover,
      textDecoration: 'underline'
    }
  },
  '&:active': {
    '& > *': {
      color: theme.dime.colors.link.primaryPressed
    }
  }
});

const StyledFilterGroup = styled(Flex)({
  minWidth: '100px',
  alignItems: 'center',
  gap: theme.dime.spacing.m,
  flexWrap: 'wrap'
});

const StyledCol = styled(Col)({
  marginBottom: theme.dime.spacing.m,
  marginRight: theme.dime.spacing.l
});

const StyledClearCol = styled(Col)({
  marginBottom: theme.dime.spacing.m,
  display: 'flex'
});

const StyledRow = styled(Row)({
  marginTop: theme.dime.spacing.l,
  maxWidth: '1000px',
  flexWrap: 'wrap',
  alignItems: 'center',
  marginRight: theme.dime.spacing.xl
});

const hasValidFilterValue = (value: any): boolean => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value))
    return value.length > 0 && value.some((v) => v !== null && v !== undefined);
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

export const formatBooleanValue = (
  value: any,
  t: (key: string) => string
): string => {
  if (value === true || value === 'true') return t('yes');
  if (value === false || value === 'false') return t('no');
  return String(value);
};

const formatFilterValue = (value: any, t: (key: string) => string): string => {
  if (Array.isArray(value)) {
    return value
      .filter((v) => v != null)
      .map((v) => formatBooleanValue(v, t))
      .join(', ');
  }
  return formatBooleanValue(value, t);
};

export const FilterHeader: React.FC<FilterHeaderProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
  resultCount,
  splitValues = false
}) => {
  const { t } = useT();
  const groupedFilters: Array<{ key: string; values: any[] }> = [];

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (!hasValidFilterValue(value)) return;

    if (splitValues && Array.isArray(value)) {
      const validValues = value.filter((item) => item != null);
      if (validValues.length > 0) {
        groupedFilters.push({ key, values: validValues });
      }
    } else {
      groupedFilters.push({ key, values: [value] });
    }
  });

  if (groupedFilters.length === 0) return null;

  const totalFilterCount = groupedFilters.reduce(
    (sum, group) => sum + group.values.length,
    0
  );

  return (
    <>
      <StyledRow align="middle" gutter={8}>
        {groupedFilters.map(({ key, values }) => (
          <StyledCol key={key}>
            <StyledFilterGroup align="middle">
              <Typography
                variant="body1"
                color={theme.dime.colors.text.primary}
              >
                {upperFirst(key)}:
              </Typography>
              {splitValues && Array.isArray(filters[key]) ? (
                <>
                  {values.map((value, index) => (
                    <RemovableTag
                      key={`${key}-${index}-${value}`}
                      variant="chip"
                      text={formatBooleanValue(value, t)}
                      onClose={() => onRemoveFilter(key, value)}
                    />
                  ))}
                </>
              ) : (
                <RemovableTag
                  variant="chip"
                  text={formatFilterValue(filters[key], t)}
                  onClose={() => onRemoveFilter(key)}
                />
              )}
            </StyledFilterGroup>
          </StyledCol>
        ))}
        {totalFilterCount > 0 && (
          <StyledClearCol>
            <StyledLinkWrapper onClick={onClearAll}>
              <Typography
                variant="body1"
                color={theme.dime.colors.link.primary}
              >
                Clear
              </Typography>
            </StyledLinkWrapper>
          </StyledClearCol>
        )}
      </StyledRow>
      {resultCount !== undefined && (
        <Row style={{ marginBottom: theme.dime.spacing.l }}>
          <Col>
            <Typography
              variant="body1"
              color={theme.dime.colors.text.secondary}
            >
              {resultCount} Results
            </Typography>
          </Col>
        </Row>
      )}
    </>
  );
};
